import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    QueueState,
    diagnoseQueue,
    formatCount,
    stageLabel,
} from '../../api/queueMonitor';

export interface IQueueStepProps {
    queue: QueueState;
    /** The poison queue belonging to this step, if there is one. */
    poison?: QueueState;
    /** The queue whose messages are currently listed below the pipeline. */
    selectedQueue: string | null;
    onSelect: (queueName: string) => void;
    now: number;
}

/** One coloured stretch of the bar under a queue card. */
interface QueueSlice {
    key: string;
    /** How many messages this stretch stands for. */
    value: number;
    /** What to call them, for the tooltip. */
    label: string;
    hint: string;
}

/**
 * What the messages on a queue are doing, split up and coloured.
 *
 * A queue with 6.124 messages on it and a queue with 6.124 messages that have each failed
 * four times are the same number and completely different situations, and the card used to
 * say the same thing about both. The four stretches are the four states a message can
 * actually be in, in the order it passes through them: waiting to be handed out, held by a
 * function, serving out the wait after a failed attempt, and given up on.
 *
 * The split rests on one thing Azure will not tell you directly - which invisible messages
 * are being worked on - so it is inferred the same way the verdict on the card is: a
 * consumer holds at most `batchSize` at a time, so anything invisible above that has a
 * failed attempt behind it. Where even that cannot be worked out, the bar says so by
 * refusing to split rather than by guessing a plausible one.
 */
const slice = (queue: QueueState, poison?: QueueState): QueueSlice[] => {
    const failed = poison?.depth ?? 0;

    const failedSlice: QueueSlice[] =
        failed > 0
            ? [
                  {
                      key: 'failed',
                      value: failed,
                      label: 'mislukt',
                      hint: `${formatCount(failed)} bericht(en) staan in ${
                          poison?.name
                      }. Niets leest die queue; ze blijven daar tot iemand ze terugzet.`,
                  },
              ]
            : [];

    if (queue.depth <= 0) {
        return failedSlice;
    }

    /*
        The peek stops at 32 messages, so on a fuller queue the visible half cannot be
        counted and nothing about the split is knowable. One stretch then, in the colour of
        "on the queue", because a made-up division of six thousand messages over four states
        is exactly the kind of confident wrongness this dashboard is meant to end.
    */
    if (queue.waiting === null || queue.waiting === undefined) {
        return [
            {
                key: 'unknown',
                value: queue.depth,
                label: 'op de queue',
                hint: 'Er staan meer berichten op deze queue dan er in één keer bekeken kunnen worden, dus hoeveel er klaarstaan en hoeveel er onzichtbaar zijn is hier niet uit elkaar te halen.',
            },
            ...failedSlice,
        ];
    }

    const hidden = queue.hidden ?? 0;
    const ceiling = queue.batchSize ?? 0;

    const working = ceiling > 0 ? Math.min(hidden, ceiling) : hidden;
    const retrying = ceiling > 0 ? Math.max(0, hidden - ceiling) : 0;

    return [
        {
            key: 'ready',
            value: queue.waiting,
            label: 'klaar om opgepakt te worden',
            hint: `${formatCount(
                queue.waiting
            )} bericht(en) staan zichtbaar klaar en zijn nog niet uitgedeeld.`,
        },
        {
            key: 'working',
            value: working,
            label: 'in behandeling',
            hint: ceiling > 0
                ? `Tot ${formatCount(
                      ceiling
                  )} bericht(en) kunnen tegelijk bij "${
                      queue.consumerFunction ?? 'de consumer'
                  }" in behandeling zijn. Zoveel zijn er nu onzichtbaar, en dat is de enige onzichtbaarheid die wel werk betekent.`
                : 'Onzichtbaar. Hoeveel er tegelijk in behandeling kunnen zijn is voor deze queue niet bekend, dus of dit werk is valt niet te zeggen.',
        },
        {
            key: 'retrying',
            value: retrying,
            label: 'wacht op een nieuwe poging',
            hint: `${formatCount(
                retrying
            )} bericht(en) zijn onzichtbaar boven wat er tegelijk in behandeling kan zijn. Die hebben dus een mislukte poging achter zich en wachten hun beurt af.`,
        },
        ...failedSlice,
    ].filter((part) => part.value > 0);
};

/**
 * The composition of a queue, as a bar.
 *
 * Always drawn, also for an empty queue, so every card in the row has the same shape and the
 * bars line up down the pipeline. An empty queue is an empty track, which reads as nothing
 * here rather than as a card that is missing something.
 */
const QueueBar = ({ parts }: { parts: QueueSlice[] }) => {
    const total = parts.reduce((sum, part) => sum + part.value, 0);

    return (
        <span
            className="queue-step__bar"
            title={
                total > 0
                    ? parts
                          .map(
                              (part) =>
                                  `${formatCount(part.value)} ${part.label}`
                          )
                          .join('\n')
                    : 'Er staat niets op deze queue.'
            }
        >
            {parts.map((part) => (
                <span
                    key={part.key}
                    className={`queue-step__slice queue-step__slice--${part.key}`}
                    style={{ width: `${(part.value / total) * 100}%` }}
                    title={part.hint}
                />
            ))}
        </span>
    );
};

/**
 * A single step in the pipeline.
 *
 * All cards are built the same way, top to bottom: the name of the queue, the count of what
 * is on it, what that queue is for, what the messages on it are doing, and the verdict. The
 * order is deliberate - the name first, because that is what you paste into a storage
 * browser and what every other screen calls this thing, and the count opposite it, because
 * it is the one number you scan a whole row for.
 *
 * The bar underneath is the part a count alone cannot say. A queue with six thousand
 * messages waiting to be picked up and one with six thousand that have each failed four
 * times are the same number and entirely different problems, and until the bar the card had
 * one line of text to tell them apart.
 */
export const QueueStep = ({
    queue,
    poison,
    selectedQueue,
    onSelect,
    now,
}: IQueueStepProps) => {
    const selected = selectedQueue === queue.name;
    const unreadable = queue.depth < 0 || !!queue.error;

    const diagnosis = diagnoseQueue(queue, now);

    const waiting =
        queue.waiting === null || queue.waiting === undefined
            ? '32+ wachten'
            : queue.waiting > 0
            ? `${queue.waiting} wachten`
            : null;

    /*
        What is invisible, described as what it is.

        "Picked up" was a guess dressed up as a measurement: everything Azure hides gets
        counted, and Azure hides a message both while a function holds it and while it waits
        out a failed attempt. Since the consumer holds at most `batchSize` at a time,
        anything above that cannot be work in progress, and calling it that turned a queue
        full of failures into a screen reporting progress.
    */
    const hidden = queue.hidden ?? 0;
    const ceiling = queue.batchSize ?? 0;

    const hiddenLabel =
        hidden <= 0
            ? null
            : ceiling > 0 && hidden > ceiling
            ? `${formatCount(hidden)} onzichtbaar`
            : `${formatCount(hidden)} in behandeling`;

    return (
        <div className="queue-step">
            <button
                type="button"
                className={`queue-step__card${
                    selected ? ' queue-step__card--selected' : ''
                }${queue.depth > 0 ? ' queue-step__card--filled' : ''}${
                    unreadable ? ' queue-step__card--broken' : ''
                }`}
                onClick={() => onSelect(queue.name)}
                aria-pressed={selected}
                title={
                    diagnosis
                        ? `${diagnosis.hint}\n\nKlik om te zien wat er in ${queue.name} staat.`
                        : `Bekijk wat er in ${queue.name} staat`
                }
            >
                {/*
                    The name and the number on one line, the two ends of the card. Every
                    other screen, every app setting and every storage browser calls this
                    queue by its name, so the name is what makes a card findable; the count
                    is what makes a row scannable.
                */}
                <span className="queue-step__top">
                    <code className="queue-step__queue">
                        <FontAwesomeIcon
                            icon="layer-group"
                            className="queue-step__icon"
                        />
                        {queue.name}
                    </code>

                    <span className="queue-step__count">
                        {unreadable ? '?' : formatCount(queue.depth)}
                    </span>
                </span>

                <span className="queue-step__label">{stageLabel(queue)}</span>

                <QueueBar parts={unreadable ? [] : slice(queue, poison)} />

                <span className="queue-step__detail">
                    {!unreadable && waiting && <span>{waiting}</span>}
                    {!unreadable && hiddenLabel && <span>{hiddenLabel}</span>}

                    {diagnosis && (
                        <span
                            className={`queue-step__verdict queue-step__verdict--${diagnosis.tone}`}
                        >
                            {diagnosis.label}
                        </span>
                    )}
                </span>
            </button>

            {/* The failed messages belong with the step they broke on, not in a separate
                list at the bottom where nobody sees the connection any more. */}
            {poison && poison.depth > 0 && (
                <button
                    type="button"
                    className={`queue-step__poison${
                        selectedQueue === poison.name
                            ? ' queue-step__poison--selected'
                            : ''
                    }`}
                    onClick={() => onSelect(poison.name)}
                    aria-pressed={selectedQueue === poison.name}
                    title={`${poison.name}: berichten die het na alle pogingen niet gehaald hebben. Niets leest deze queue; ze blijven staan tot iemand ze terugzet.`}
                >
                    {formatCount(poison.depth)} mislukt
                </button>
            )}
        </div>
    );
};
